/**
 * MarcoLegalDatos — Manual interno para el equipo Entretur sobre el marco
 * legal en el que opera el sistema NARA Tourism. NO es la política pública
 * para clientes finales (esa se redacta cuando el abogado la apruebe).
 *
 * Objetivo: que cualquier miembro del equipo (cajero, operador, admin)
 * entienda qué leyes aplican, qué hace el sistema y qué se espera de ellos.
 */
import React from 'react';
import {
  Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails,
  List, ListItem, ListItemIcon, ListItemText, Chip, Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GavelIcon from '@mui/icons-material/Gavel';
import LockIcon from '@mui/icons-material/Lock';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import VisibilityIcon from '@mui/icons-material/Visibility';

const SECTIONS = [
  {
    id: 'porque',
    icon: <HelpOutlineIcon />,
    color: '#0ea5e9',
    title: '¿Por qué existe esta sección?',
    content: (
      <>
        <Typography paragraph sx={{ fontSize: 13 }}>
          Como operadora turística, Entretur maneja diariamente datos sensibles de sus turistas:
          <strong> nombre, cédula, pasaporte, foto, datos de pago</strong>. En Colombia, el tratamiento
          de estos datos está regulado por la <strong>Ley 1581 de 2012 (Habeas Data)</strong>.
        </Typography>
        <Typography paragraph sx={{ fontSize: 13 }}>
          El sistema <strong>NARA Tourism</strong> está diseñado para cumplir esa ley y proteger
          a Entretur de sanciones de la Superintendencia de Industria y Comercio (SIC).
          Esta página explica al equipo cómo opera el sistema y qué debe hacer cada persona.
        </Typography>
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fef3c7', border: '1px solid #fcd34d', mt: 1 }}>
          <Typography sx={{ fontSize: 12, color: '#78350f' }}>
            ⚠ <strong>Esta es información interna para el equipo Entretur.</strong> No reemplaza la
            política de privacidad pública que se entregará a los turistas (esa la redacta el abogado).
          </Typography>
        </Box>
      </>
    ),
  },
  {
    id: 'leyes',
    icon: <GavelIcon />,
    color: '#8b5cf6',
    title: 'Leyes y normas que nos aplican',
    content: (
      <List dense disablePadding>
        {[
          { law: 'Ley 1581 de 2012', desc: 'Protección de Datos Personales (Habeas Data) — núcleo de toda la operación digital con clientes.' },
          { law: 'Decreto 1377 de 2013', desc: 'Reglamentario de la Ley 1581 — detalla cómo aplicar la ley en la práctica.' },
          { law: 'Resoluciones DIMAR', desc: 'Dirección General Marítima — identificación obligatoria de pasajeros en operaciones náuticas. Manifiesto de embarque.' },
          { law: 'PCI-DSS', desc: 'Estándar de seguridad para datos de tarjeta. NARA NO almacena tarjetas — eso lo hace Bold (pasarela certificada).' },
          { law: 'Ley 1480 de 2011', desc: 'Estatuto del Consumidor — reservas, devoluciones, derechos del cliente.' },
        ].map((l, i) => (
          <ListItem key={i} sx={{ alignItems: 'flex-start', py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 30, mt: 0.5 }}>
              <GavelIcon sx={{ fontSize: 14, color: '#8b5cf6' }} />
            </ListItemIcon>
            <ListItemText
              primary={l.law}
              secondary={l.desc}
              primaryTypographyProps={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}
              secondaryTypographyProps={{ fontSize: 12, color: '#475569' }}
            />
          </ListItem>
        ))}
      </List>
    ),
  },
  {
    id: 'datos',
    icon: <AssignmentIcon />,
    color: '#0d9488',
    title: '¿Qué datos recolectamos?',
    content: (
      <>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          {[
            { cat: 'Identificación', items: ['Nombre completo', 'Cédula / pasaporte', 'Foto del titular (al embarcar)'], sensible: true },
            { cat: 'Contacto', items: ['WhatsApp', 'Email', 'Hotel donde se hospeda'], sensible: false },
            { cat: 'Operativo', items: ['Código de reserva', 'Fecha de embarque', 'Pax (adultos, niños, bebés)', 'Tour seleccionado'], sensible: false },
            { cat: 'Financiero', items: ['Monto pagado', 'Método (efectivo/tarjeta/BOLD)', 'Transaction ID', '❌ NO guardamos número de tarjeta'], sensible: 'medio' },
          ].map((g, i) => (
            <Box key={i} sx={{ p: 1.5, borderRadius: 2,
              border: '1px solid', borderColor: g.sensible === true ? '#fca5a5' : g.sensible === 'medio' ? '#fcd34d' : '#cbd5e1',
              bgcolor: g.sensible === true ? '#fef2f2' : g.sensible === 'medio' ? '#fef3c7' : '#f8fafc' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{g.cat}</Typography>
                {g.sensible === true && <Chip label="Sensible" size="small" sx={{ height: 16, fontSize: 9, bgcolor: '#fee2e2', color: '#991b1b' }} />}
                {g.sensible === 'medio' && <Chip label="Restringido" size="small" sx={{ height: 16, fontSize: 9, bgcolor: '#fef3c7', color: '#92400e' }} />}
              </Box>
              {g.items.map((it, j) => (
                <Typography key={j} sx={{ fontSize: 11, color: '#475569', lineHeight: 1.7 }}>• {it}</Typography>
              ))}
            </Box>
          ))}
        </Box>
      </>
    ),
  },
  {
    id: 'retencion',
    icon: <HistoryIcon />,
    color: '#f59e0b',
    title: '¿Cuánto tiempo conservamos cada cosa?',
    content: (
      <>
        <Typography paragraph sx={{ fontSize: 13 }}>
          La Ley 1581 exige que los datos personales se conserven <strong>solo por el tiempo necesario</strong> para
          cumplir la finalidad. Por eso el sistema gestiona la retención de forma diferenciada:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[
            { tipo: 'Reservas, pagos, comisiones, cierres de muelle', tiempo: '♾️ INDEFINIDO', color: '#0d9488',
              razon: 'Registros operativos y contables. Obligación de retención fiscal (DIAN: 5 años mínimo).' },
            { tipo: 'Datos agregados (nacionalidad, edad agrupada, tour)', tiempo: '♾️ INDEFINIDO', color: '#0d9488',
              razon: 'Sirven para reportes estadísticos sin identificar personas. Anónimos.' },
            { tipo: 'Foto del pasaporte + número de documento', tiempo: '⏱ 12 MESES', color: '#dc2626',
              razon: 'Después se anonimizan automáticamente (job nocturno). La fila se mantiene, pero foto+número se borran.' },
            { tipo: 'API keys de pasarelas de pago (Bold, etc.)', tiempo: '🔒 CIFRADAS AES-GCM', color: '#0ea5e9',
              razon: 'Nunca en texto plano. Solo el backend puede descifrarlas con clave maestra.' },
            { tipo: 'Conversaciones de WhatsApp', tiempo: '⏱ 365 DÍAS', color: '#f59e0b',
              razon: 'Job limpia conversaciones más viejas. Memoria semántica por categorías.' },
          ].map((r, i) => (
            <Box key={i} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', flex: 1 }}>{r.tipo}</Typography>
                <Chip label={r.tiempo} size="small" sx={{ bgcolor: r.color + '20', color: r.color, fontWeight: 700, fontSize: 11 }} />
              </Box>
              <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.5 }}>{r.razon}</Typography>
            </Box>
          ))}
        </Box>
      </>
    ),
  },
  {
    id: 'equipo',
    icon: <CheckCircleIcon />,
    color: '#16a34a',
    title: '¿Qué debe hacer el equipo Entretur?',
    content: (
      <List dense disablePadding>
        {[
          { do: 'Al tomar la foto del titular', detail: 'Explicar verbalmente: "Necesito tu pasaporte/cédula para el registro de embarque según normativa DIMAR. Tu foto se conserva 12 meses". El turista debe estar al tanto.' },
          { do: 'NO exportar fotos del sistema', detail: 'Nunca enviar fotos del sistema NARA a celular personal, WhatsApp privado o email. Solo se manejan dentro del sistema.' },
          { do: 'Si un turista pregunta cómo eliminar sus datos', detail: 'Dirigirlo al canal oficial (email a definir por la empresa, ej: habeasdata@entretur.com). No prometer eliminación inmediata sin consultar.' },
          { do: 'No compartir información operativa', detail: 'Datos de turistas, reservas, montos no se comparten con personas ajenas a la operación (otros operadores, familia, etc.).' },
          { do: 'Al ver datos sensibles en el panel', detail: 'Solo se accede para la finalidad operativa declarada. No es para curiosidad ni para fines distintos.' },
          { do: 'Si hay error o brecha de seguridad', detail: 'Reportar inmediatamente al admin de Entretur. La SIC exige notificación dentro de las 72h siguientes a la detección.' },
        ].map((d, i) => (
          <ListItem key={i} sx={{ alignItems: 'flex-start', py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 30, mt: 0.5 }}>
              <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
            </ListItemIcon>
            <ListItemText
              primary={d.do}
              secondary={d.detail}
              primaryTypographyProps={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}
              secondaryTypographyProps={{ fontSize: 12, color: '#475569' }}
            />
          </ListItem>
        ))}
      </List>
    ),
  },
  {
    id: 'sistema',
    icon: <SmartToyIcon />,
    color: '#7c3aed',
    title: '¿Qué hace NARA automáticamente?',
    content: (
      <List dense disablePadding>
        {[
          { feat: 'Cifrado AES-GCM 256-bit', desc: 'Las API keys de Bold, Wompi, ePayco y demás pasarelas se almacenan cifradas. Nunca en texto plano.' },
          { feat: 'Anonimización automática mensual', desc: 'Job nocturno (4:15 AM UTC, diario) anonimiza fotos y documentos > 12 meses. Conserva metadata operativa.' },
          { feat: 'Auditoría completa', desc: 'Cada acción sensible queda registrada: quién, cuándo, qué cambió, desde dónde. Trazabilidad total.' },
          { feat: 'Validación con IA del documento', desc: 'Claude Vision valida que el nombre en el pasaporte coincida con el titular de la reserva. Detecta documentos vencidos.' },
          { feat: 'Webhooks firmados con HMAC', desc: 'Notificaciones de Bold se validan con firma SHA-256 antes de aceptarse. Evita pagos falsificados.' },
          { feat: 'Aislamiento por empresa (multi-tenant)', desc: 'Cada empresa solo ve sus propios datos. Garantía técnica de separación.' },
        ].map((f, i) => (
          <ListItem key={i} sx={{ alignItems: 'flex-start', py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 30, mt: 0.5 }}>
              <SmartToyIcon sx={{ fontSize: 16, color: '#7c3aed' }} />
            </ListItemIcon>
            <ListItemText
              primary={f.feat}
              secondary={f.desc}
              primaryTypographyProps={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}
              secondaryTypographyProps={{ fontSize: 12, color: '#475569' }}
            />
          </ListItem>
        ))}
      </List>
    ),
  },
  {
    id: 'riesgos',
    icon: <ReportProblemIcon />,
    color: '#dc2626',
    title: 'Riesgos por incumplimiento',
    content: (
      <>
        <Typography paragraph sx={{ fontSize: 13 }}>
          Cumplir la Ley 1581 no es opcional. Las sanciones por incumplimiento son significativas:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[
            { who: 'SIC — Sanciones administrativas', desc: 'Multas hasta 2.000 SMMLV (~$2.600 millones COP) por infracciones graves. Suspensión temporal de tratamiento de datos.' },
            { who: 'Demandas civiles', desc: 'Cualquier titular afectado puede demandar por daños y perjuicios. No prescribe rápido.' },
            { who: 'DIMAR', desc: 'Suspensión de la operación marítima por irregularidades en el manifiesto de embarque o falta de identificación de pasajeros.' },
            { who: 'Reputacional', desc: 'Una brecha de datos publicada en medios puede destruir la confianza de los turistas. Recuperar reputación toma años.' },
          ].map((r, i) => (
            <Box key={i} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#991b1b' }}>{r.who}</Typography>
              <Typography sx={{ fontSize: 12, color: '#7f1d1d' }}>{r.desc}</Typography>
            </Box>
          ))}
        </Box>
      </>
    ),
  },
  {
    id: 'donde',
    icon: <VisibilityIcon />,
    color: '#0891b2',
    title: '¿Dónde se aplica en la app?',
    content: (
      <>
        <Typography paragraph sx={{ fontSize: 13 }}>
          Estos lugares de la app manejan datos regulados — el sistema te mostrará un
          indicador <strong>🔒</strong> cuando estés en una pantalla con datos sensibles:
        </Typography>
        <List dense disablePadding>
          {[
            { lugar: 'Muelle → Check-in', desc: 'Captura de foto del titular + OCR del documento. Operador debe informar al turista.' },
            { lugar: 'Cierre de Muelle', desc: 'Documento auditado. Firma de cajero y supervisor con timestamp. Datos quedan inmutables.' },
            { lugar: 'Pasarelas de Pago', desc: 'API keys cifradas AES-GCM. Nunca en texto plano. Webhook validado con HMAC.' },
            { lugar: 'Reservaciones / Booking', desc: 'Manejo de cédulas y pasaportes. Solo personal autorizado puede ver el detalle.' },
            { lugar: 'Trazabilidad / Auditoría', desc: 'Registro inmutable de toda acción del equipo sobre datos sensibles.' },
            { lugar: 'Cobro con tarjeta', desc: 'NARA no guarda tarjetas. Bold procesa. NARA solo ve transaction_id.' },
          ].map((p, i) => (
            <ListItem key={i} sx={{ alignItems: 'flex-start', py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 30, mt: 0.5 }}>
                <LockIcon sx={{ fontSize: 14, color: '#0891b2' }} />
              </ListItemIcon>
              <ListItemText
                primary={p.lugar}
                secondary={p.desc}
                primaryTypographyProps={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}
                secondaryTypographyProps={{ fontSize: 12, color: '#475569' }}
              />
            </ListItem>
          ))}
        </List>
      </>
    ),
  },
];

export default function MarcoLegalDatos() {
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
            color: '#fbbf24',
          }}>
            <GavelIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800, color: '#0f172a' }}>
              Marco Legal · Datos Personales
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#64748b' }}>
              Manual interno para el equipo de Entretur · Versión 1.0 · 2026
            </Typography>
          </Box>
        </Box>

        <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', mt: 2 }}>
          <Typography sx={{ fontSize: 13, color: '#1e3a8a', lineHeight: 1.6 }}>
            Esta sección explica el marco legal que rige el manejo de datos personales en NARA Tourism.
            Léelo completo si manejas información de turistas en muelle, recepción, finanzas o
            administración. <strong>El sistema NARA está diseñado para cumplir esta normativa
            automáticamente</strong>, pero el equipo humano también tiene responsabilidades.
          </Typography>
        </Paper>
      </Box>

      {/* Accordions con el contenido */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {SECTIONS.map((sec, i) => (
          <Accordion key={sec.id} defaultExpanded={i === 0}
            sx={{
              borderRadius: 2, '&:before': { display: 'none' },
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: 1.5, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  bgcolor: sec.color + '15', color: sec.color,
                }}>
                  {React.cloneElement(sec.icon, { sx: { fontSize: 18 } })}
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                  {sec.title}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {sec.content}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Footer */}
      <Divider sx={{ my: 3 }} />
      <Box sx={{ textAlign: 'center', color: '#94a3b8' }}>
        <Typography sx={{ fontSize: 11 }}>
          Última revisión: 29 de mayo de 2026 · v1.0
        </Typography>
        <Typography sx={{ fontSize: 10, mt: 0.5 }}>
          Documento interno · No es la política de privacidad pública para clientes finales
        </Typography>
      </Box>
    </Box>
  );
}
